import { useEffect, useRef, useMemo } from "react";
import { Animated, Easing, Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { useLoopValue, usePulseValue } from "@/components/steps/poc-review/useAnimationHelpers";

const TEAL = "#45D2B0";
const PINK = "#E748F5";
const _BG = "#0A1628";
const SCENE = 280;
const CENTER = SCENE / 2;

// ---------------------------------------------------------------------------
// Pulsing ring
// ---------------------------------------------------------------------------

function PulsingRing({
    size,
    color,
    borderW,
    duration,
    minO,
    maxO
}: {
    size: number;
    color: string;
    borderW: number;
    duration: number;
    minO: number;
    maxO: number;
}) {
    const opacity = usePulseValue(duration, minO, maxO);
    const off = (SCENE - size) / 2;
    return (
        <Animated.View
            style={{
                position: "absolute",
                top: off,
                left: off,
                width: size,
                height: size,
                borderRadius: size / 2,
                borderWidth: borderW,
                borderColor: color,
                opacity
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Spinning logo (4-quadrant leaf)
// ---------------------------------------------------------------------------

function SpinningLogo() {
    const spin = useLoopValue(9000);
    const scale = usePulseValue(2000, 1, 1.1);
    const sz = 66;
    const half = sz / 2;
    const off = CENTER - half;
    const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: off,
                left: off,
                width: sz,
                height: sz,
                transform: [{ rotate }, { scale }]
            }}
        >
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: TEAL,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: PINK,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: PINK,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: TEAL,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </Animated.View>
    );
}

// ---------------------------------------------------------------------------
// Scan arc (rotating gradient wedge)
// ---------------------------------------------------------------------------

function ScanArc() {
    const spin = useLoopValue(3000);
    const opacity = usePulseValue(3000, 0, 0.6);
    const arcSz = 180;
    const off = (SCENE - arcSz) / 2;
    const rotate = spin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: off,
                left: off,
                width: arcSz,
                height: arcSz,
                borderRadius: arcSz / 2,
                overflow: "hidden",
                transform: [{ rotate }],
                opacity
            }}
        >
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: arcSz / 2,
                    width: arcSz / 2,
                    height: arcSz / 2,
                    backgroundColor: "rgba(69,210,176,0.25)"
                }}
            />
        </Animated.View>
    );
}

// ---------------------------------------------------------------------------
// Orbiting dot
// ---------------------------------------------------------------------------

function OrbitDot({
    radius,
    speed,
    startAngle,
    size,
    color
}: {
    radius: number;
    speed: number;
    startAngle: number;
    size: number;
    color: string;
}) {
    const anim = useLoopValue(speed);

    // Pre-compute 9 evenly-spaced points for smooth interpolation
    const points = useMemo(() => {
        const inp: number[] = [];
        const outX: number[] = [];
        const outY: number[] = [];
        for (let i = 0; i <= 8; i++) {
            const frac = i / 8;
            const angleDeg = startAngle + frac * 360;
            const rad = ((angleDeg - 90) * Math.PI) / 180;
            inp.push(frac);
            outX.push(Math.cos(rad) * radius);
            outY.push(Math.sin(rad) * radius);
        }
        return { inp, outX, outY };
    }, [radius, startAngle]);

    const translateX = anim.interpolate({ inputRange: points.inp, outputRange: points.outX });
    const translateY = anim.interpolate({ inputRange: points.inp, outputRange: points.outY });

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: CENTER - size / 2,
                left: CENTER - size / 2,
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: color,
                transform: [{ translateX }, { translateY }]
            }}
        />
    );
}

// ---------------------------------------------------------------------------
// Orbiting pill (two-tone capsule)
// ---------------------------------------------------------------------------

function OrbitPill({
    radius,
    speed,
    startAngle,
    color1,
    color2
}: {
    radius: number;
    speed: number;
    startAngle: number;
    color1: string;
    color2: string;
}) {
    const anim = useLoopValue(speed);
    const bob = usePulseValue(2100, 0, -4);

    const points = useMemo(() => {
        const inp: number[] = [];
        const outX: number[] = [];
        const outY: number[] = [];
        for (let i = 0; i <= 8; i++) {
            const frac = i / 8;
            const angleDeg = startAngle + frac * 360;
            const rad = ((angleDeg - 90) * Math.PI) / 180;
            inp.push(frac);
            outX.push(Math.cos(rad) * radius);
            outY.push(Math.sin(rad) * radius);
        }
        return { inp, outX, outY };
    }, [radius, startAngle]);

    const translateX = anim.interpolate({ inputRange: points.inp, outputRange: points.outX });
    const translateY = anim.interpolate({ inputRange: points.inp, outputRange: points.outY });

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: CENTER - 5,
                left: CENTER - 10,
                width: 20,
                height: 10,
                borderRadius: 5,
                overflow: "hidden",
                flexDirection: "row",
                transform: [
                    { translateX },
                    { translateY },
                    { translateY: bob },
                    { rotate: "-22deg" }
                ]
            }}
        >
            <View style={{ flex: 1, backgroundColor: color1 }} />
            <View style={{ flex: 1, backgroundColor: color2 }} />
        </Animated.View>
    );
}

// ---------------------------------------------------------------------------
// Orbiting plus sign
// ---------------------------------------------------------------------------

function OrbitPlus({
    radius,
    speed,
    startAngle,
    color,
    spinSpeed
}: {
    radius: number;
    speed: number;
    startAngle: number;
    color: string;
    spinSpeed: number;
}) {
    const orbit = useLoopValue(speed);
    const selfSpin = useLoopValue(spinSpeed);

    const points = useMemo(() => {
        const inp: number[] = [];
        const outX: number[] = [];
        const outY: number[] = [];
        for (let i = 0; i <= 8; i++) {
            const frac = i / 8;
            const angleDeg = startAngle + frac * 360;
            const rad = ((angleDeg - 90) * Math.PI) / 180;
            inp.push(frac);
            outX.push(Math.cos(rad) * radius);
            outY.push(Math.sin(rad) * radius);
        }
        return { inp, outX, outY };
    }, [radius, startAngle]);

    const translateX = orbit.interpolate({ inputRange: points.inp, outputRange: points.outX });
    const translateY = orbit.interpolate({ inputRange: points.inp, outputRange: points.outY });
    const rotate = selfSpin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });

    const sz = 12;
    const bar = 3;

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: CENTER - sz / 2,
                left: CENTER - sz / 2,
                width: sz,
                height: sz,
                transform: [{ translateX }, { translateY }, { rotate }]
            }}
        >
            <View
                style={{
                    position: "absolute",
                    top: (sz - bar) / 2,
                    left: 0,
                    width: sz,
                    height: bar,
                    borderRadius: bar / 2,
                    backgroundColor: color
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: (sz - bar) / 2,
                    width: bar,
                    height: sz,
                    borderRadius: bar / 2,
                    backgroundColor: color
                }}
            />
        </Animated.View>
    );
}

// ---------------------------------------------------------------------------
// Orbiting heart
// ---------------------------------------------------------------------------

function OrbitHeart({
    radius,
    speed,
    startAngle,
    color
}: {
    radius: number;
    speed: number;
    startAngle: number;
    color: string;
}) {
    const orbit = useLoopValue(speed);
    const beat = usePulseValue(1300, 1, 1.35);

    const points = useMemo(() => {
        const inp: number[] = [];
        const outX: number[] = [];
        const outY: number[] = [];
        for (let i = 0; i <= 8; i++) {
            const frac = i / 8;
            const angleDeg = startAngle + frac * 360;
            const rad = ((angleDeg - 90) * Math.PI) / 180;
            inp.push(frac);
            outX.push(Math.cos(rad) * radius);
            outY.push(Math.sin(rad) * radius);
        }
        return { inp, outX, outY };
    }, [radius, startAngle]);

    const translateX = orbit.interpolate({ inputRange: points.inp, outputRange: points.outX });
    const translateY = orbit.interpolate({ inputRange: points.inp, outputRange: points.outY });

    // Heart shape from two rotated rounded squares
    const sz = 10;

    return (
        <Animated.View
            style={{
                position: "absolute",
                top: CENTER - sz,
                left: CENTER - sz,
                width: sz * 2,
                height: sz * 2,
                transform: [{ translateX }, { translateY }, { scale: beat }],
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            {/* Simple heart via two overlapping circles + rotated square */}
            <View
                style={{
                    width: sz,
                    height: sz,
                    backgroundColor: color,
                    borderRadius: sz / 2,
                    position: "absolute",
                    top: 0,
                    left: sz / 4
                }}
            />
            <View
                style={{
                    width: sz,
                    height: sz,
                    backgroundColor: color,
                    borderRadius: sz / 2,
                    position: "absolute",
                    top: 0,
                    right: sz / 4
                }}
            />
            <View
                style={{
                    width: sz,
                    height: sz,
                    backgroundColor: color,
                    position: "absolute",
                    top: sz / 3,
                    transform: [{ rotate: "45deg" }]
                }}
            />
        </Animated.View>
    );
}

// ---------------------------------------------------------------------------
// EKG sweep bar
// ---------------------------------------------------------------------------

function _EkgBar() {
    const sweep = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        const a = Animated.loop(
            Animated.sequence([
                Animated.timing(sweep, {
                    toValue: 1,
                    duration: 1800,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: false
                }),
                Animated.delay(600),
                Animated.timing(sweep, { toValue: 0, duration: 0, useNativeDriver: false })
            ])
        );
        a.start();
        return () => a.stop();
    }, [sweep]);

    const width = sweep.interpolate({ inputRange: [0, 1], outputRange: ["0%", "100%"] });

    return (
        <View style={styles.ekgTrack}>
            <Animated.View style={[styles.ekgFill, { width }]} />
        </View>
    );
}

// ---------------------------------------------------------------------------
// Thinking dots
// ---------------------------------------------------------------------------

function ThinkingDots() {
    const d1 = usePulseValue(1600, 0.15, 1);
    const d2 = useRef(new Animated.Value(0.15)).current;
    const d3 = useRef(new Animated.Value(0.15)).current;

    useEffect(() => {
        const a2 = Animated.loop(
            Animated.sequence([
                Animated.delay(400),
                Animated.timing(d2, {
                    toValue: 1,
                    duration: 400,
                    useNativeDriver: Platform.OS !== "web"
                }),
                Animated.timing(d2, {
                    toValue: 0.15,
                    duration: 400,
                    useNativeDriver: Platform.OS !== "web"
                })
            ])
        );
        const a3 = Animated.loop(
            Animated.sequence([
                Animated.delay(800),
                Animated.timing(d3, {
                    toValue: 1,
                    duration: 400,
                    useNativeDriver: Platform.OS !== "web"
                }),
                Animated.timing(d3, {
                    toValue: 0.15,
                    duration: 400,
                    useNativeDriver: Platform.OS !== "web"
                })
            ])
        );
        a2.start();
        a3.start();
        return () => {
            a2.stop();
            a3.stop();
        };
    }, [d2, d3]);

    return (
        <View style={styles.dotsRow}>
            <Text style={styles.label}>AI Co-Pilot thinking</Text>
            <Animated.Text style={[styles.dot, { color: TEAL, opacity: d1 }]}>·</Animated.Text>
            <Animated.Text style={[styles.dot, { color: TEAL, opacity: d2 }]}>·</Animated.Text>
            <Animated.Text style={[styles.dot, { color: PINK, opacity: d3 }]}>·</Animated.Text>
        </View>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AiVerificationAnimation() {
    return (
        <View style={styles.root}>
            <View style={styles.scene}>
                {/* Rings */}
                <PulsingRing
                    size={268}
                    color={PINK}
                    borderW={1}
                    duration={2800}
                    minO={0.07}
                    maxO={0.2}
                />
                <PulsingRing
                    size={192}
                    color={TEAL}
                    borderW={1.5}
                    duration={2000}
                    minO={0.18}
                    maxO={0.42}
                />
                <PulsingRing
                    size={90}
                    color={TEAL}
                    borderW={2}
                    duration={1500}
                    minO={0.1}
                    maxO={0.4}
                />

                <ScanArc />
                <SpinningLogo />

                {/* Inner orbit dots */}
                <OrbitDot radius={42} speed={3500} startAngle={0} size={6} color={TEAL} />
                <OrbitDot radius={42} speed={3500} startAngle={90} size={6} color={PINK} />
                <OrbitDot radius={42} speed={3500} startAngle={180} size={5} color={TEAL} />
                <OrbitDot radius={42} speed={3500} startAngle={270} size={5} color={PINK} />

                {/* Outer orbit: pills, hearts, plus signs */}
                <OrbitPill radius={105} speed={11000} startAngle={0} color1={TEAL} color2={PINK} />
                <OrbitHeart radius={98} speed={15000} startAngle={120} color={PINK} />
                <OrbitPlus
                    radius={105}
                    speed={11000}
                    startAngle={240}
                    color={TEAL}
                    spinSpeed={7000}
                />
                <OrbitPill radius={92} speed={9000} startAngle={62} color1={PINK} color2={TEAL} />
                <OrbitPlus
                    radius={102}
                    speed={13000}
                    startAngle={188}
                    color={PINK}
                    spinSpeed={5000}
                />
                <OrbitHeart radius={110} speed={19000} startAngle={308} color={TEAL} />
            </View>

            <ThinkingDots />
        </View>
    );
}

const styles = StyleSheet.create({
    root: {
        paddingTop: 16,
        paddingBottom: 14,
        paddingHorizontal: 16,
        alignItems: "center",
        gap: 8,
        overflow: "hidden",
        marginTop: 16
    },
    scene: {
        width: SCENE,
        height: SCENE
    },
    ekgTrack: {
        width: "85%",
        height: 3,
        backgroundColor: "rgba(69,210,176,0.15)",
        borderRadius: 1.5,
        overflow: "hidden"
    },
    ekgFill: {
        height: "100%",
        backgroundColor: TEAL,
        borderRadius: 1.5
    },
    dotsRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2
    },
    label: {
        color: "rgba(0,0,0,0.35)",
        fontSize: 10,
        letterSpacing: 1.5,
        textTransform: "uppercase",
        marginRight: 8
    },
    dot: {
        fontSize: 22,
        lineHeight: 24,
        fontWeight: "700"
    }
});
